// KYC block modal (bunkers KYC gate, July 2026).
//
// Shown when a trader tries to recap a bunker enquiry whose CRM-linked
// partners aren't all KYC-cleared. It lists each blocking partner with a
// per-reason label and -- when at least one partner is sendable (awaiting_kyc)
// -- offers a "Notify KYC approver" button that raises clearance requests and
// pings every KYC approver. Flagged / unlinked partners are surfaced but are
// not sendable through this channel.
//
// Reuses the ``fef-modal*`` chrome. All layout is scoped to ``kyc-block-*``
// classes (no <form className="hms-stacked-form"> element) so it can't trip the app-wide unscoped form{}
// CSS leak.

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldHalved,
  faXmark,
  faTriangleExclamation,
  faUserSlash,
  faHourglassHalf,
} from '@fortawesome/free-solid-svg-icons';
import './KYCBlockModal.css';

const REASON_META = {
  awaiting_kyc: {
    icon: faHourglassHalf,
    className: 'kyc-block-reason--awaiting',
    label: 'will be sent to the KYC approver',
  },
  flagged: {
    icon: faTriangleExclamation,
    className: 'kyc-block-reason--flagged',
    label: 'blocked — flagged by compliance',
  },
  unlinked_customer: {
    icon: faUserSlash,
    className: 'kyc-block-reason--unlinked',
    label: 'link this customer to a CRM partner first',
  },
};

const KYCBlockModal = ({
  isOpen,
  blockedPartners = [],
  canRequest = false,
  onClose,
  onNotify,
  notifying = false,
  error = null,
  bypassActive = false,
}) => {
  if (!isOpen) return null;

  return (
    <div
      className="fef-modal-overlay"
      onClick={() => !notifying && onClose?.()}
    >
      <div
        className="fef-modal kyc-block-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="kyc-block-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="fef-modal-header">
          <h3 className="fef-modal-title" id="kyc-block-modal-title">
            <FontAwesomeIcon icon={faShieldHalved} /> KYC clearance required
          </h3>
          <button
            type="button"
            className="fef-modal-close"
            onClick={onClose}
            disabled={notifying}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </header>

        <div className="fef-modal-body">
          <p className="fef-modal-prompt">
            This enquiry can't be recapped yet. The following partners must
            hold portal KYC approval first:
          </p>

          {bypassActive && (
            <div className="kyc-block-bypass-note" role="status">
              <FontAwesomeIcon icon={faTriangleExclamation} /> The KYC gate is
              stood down for testing, so only Compliance-blocked partners still
              stop this enquiry.
            </div>
          )}

          <ul className="kyc-block-list">
            {blockedPartners.map((p, idx) => {
              const meta = REASON_META[p.reason] || REASON_META.awaiting_kyc;
              return (
                <li
                  key={p.profile_id ?? `unlinked-${idx}`}
                  className="kyc-block-item"
                >
                  <span className="kyc-block-partner">{p.name}</span>
                  <span className={`kyc-block-reason ${meta.className}`}>
                    <FontAwesomeIcon icon={meta.icon} /> {meta.label}
                  </span>
                  {/* The cause, in words. Three different states all surface as
                      "awaiting_kyc" -- an approved case linked to the wrong
                      partner (or none), a legacy "Approved*" partner, or a
                      partner genuinely not screened yet. Without this line the
                      trader is told the deal is blocked and nothing else. */}
                  {p.detail && (
                    <span className="kyc-block-detail">{p.detail}</span>
                  )}
                </li>
              );
            })}
          </ul>

          {error && (
            <div className="fef-modal-error" role="alert">{error}</div>
          )}
        </div>

        <footer className="fef-modal-footer">
          <button
            type="button"
            className="fef-modal-btn fef-modal-btn-secondary"
            onClick={onClose}
            disabled={notifying}
          >
            Close
          </button>
          {canRequest && (
            <button
              type="button"
              className="fef-modal-btn fef-modal-btn-primary"
              onClick={onNotify}
              disabled={notifying}
            >
              {notifying ? (
                <>
                  <span
                    className="fef-modal-btn-spinner"
                    aria-hidden="true"
                  />
                  <span>Notifying…</span>
                </>
              ) : (
                <>Notify KYC approver</>
              )}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
};

export default KYCBlockModal;
